import {
  buildResumeStructureIndex,
  cleanResumeTextSeparators,
  hasCompleteResumeDocumentStructure as hasCompleteExtractionStructure,
} from '../shared/resume-structure.js';

export const RESUME_SECTION_ALIASES = [
  'professional summary', 'career summary', 'summary', 'profile', 'career objective',
  'areas of expertise', 'area of expertise', 'key competencies',
  'core competencies', 'core skills', 'technical skills', 'skills',
  'professional experience', 'work experience', 'employment history', 'experience',
  'education', 'academic background', 'projects', 'project experience',
  'certifications', 'certificates', 'awards', 'languages',
  '个人简介', '个人概况', '专业概要', '职业概述', '求职目标', '专业领域', '关键能力', '核心能力', '核心技能',
  '专业技能', '技能', '专业经验', '专业经历', '工作经验', '工作经历', '职业经历', '教育背景', '教育经历',
  '技术技能', '项目经验', '项目经历', '教育', '证书', '认证', '荣誉', '语言能力', '个人信息', '联系方式',
];

export function resumeHeading(line) {
  const markdown = line.match(/^#{1,4}\s+(.+)$/);
  if (markdown) return markdown[1].trim();
  const clean = line.replace(/^\*\*(.+)\*\*$/, '$1').replace(/[：:]$/, '').trim();
  const normalized = clean.toLocaleLowerCase().replace(/\s+/g, ' ');
  if (normalized.length > 48) return null;
  return RESUME_SECTION_ALIASES.some((alias) => normalized === alias
    || normalized.startsWith(`${alias} `) || normalized.endsWith(` ${alias}`)) ? clean : null;
}

export function cleanResumeDisplayText(text = '') {
  return cleanResumeTextSeparators(String(text)
    .replace(/\u200b/g, ''));
}

export function hasCompleteResumeDocumentStructure(asset) {
  return hasCompleteExtractionStructure(asset?.extraction || asset || {});
}

export function resumeEntryLine(line) {
  const clean = String(line || '').replace(/\u200b/g, '').trim();
  const date = clean.match(/\b(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+\d{4}\s*(?:[-–—]|to)\s*(?:Present|Current|今|至今|\b(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+\d{4})/i)
    || clean.match(/\b\d{4}[./年-]\d{1,2}(?:月)?\s*(?:[-–—]|至今|至\s*今|to)\s*(?:Present|Current|今|至今|至\s*今|\d{4}[./年-]\d{1,2}(?:月)?|\d{1,2}月?)/iu)
    || clean.match(/\b\d{4}\s*(?:[-–—]|to)\s*(?:Present|Current|今|至今|至\s*今|\d{4})\b/iu);
  if (!date || clean.length > 150 || /^(?:summary|profile|skills|education|experience|工作经历|教育背景)$/i.test(clean)) return null;
  const title = clean.slice(0, date.index).replace(/[|·•,:：-]+\s*$/, '').trim();
  if (!title || title.length < 3) return null;
  return { title, date: date[0].trim() };
}

export function isProfessionalExperienceHeading(title = '') {
  const normalized = String(title).toLocaleLowerCase().replace(/[：:]$/, '').trim();
  return ['professional experience', 'work experience', 'employment history', 'experience', '专业经验', '专业经历', '工作经验', '工作经历', '职业经历'].includes(normalized);
}

const LOCATION_HINT = /^(?:singapore|malaysia|china|hong kong|taiwan|japan|korea|united states|usa|uk|united kingdom|australia|germany|france|india|indonesia|thailand|vietnam|philippines|uae|dubai|london|shanghai|beijing|shenzhen|hangzhou|guangzhou|tokyo|seoul|sydney|melbourne|jakarta|bangkok|kuala lumpur|penang|johor|selangor|singapore\s*pr|新加坡|中国|马来西亚|香港|台湾|日本|韩国|英国|美国|澳大利亚|德国|法国|印度|印尼|泰国|越南|菲律宾|迪拜|伦敦|上海|北京|深圳|广州|杭州|成都|武汉|南京|苏州|厦门|青岛|天津|重庆|西安|海外|亚太|全球)$/i;

export function resumeCompanyLine(line = '') {
  const clean = String(line).trim();
  if (!clean || /^[•●▪◦·.*-]\s*/.test(clean)) return null;
  const match = clean.match(/^(.{2,80}?)[，,]\s*([^，,]{2,24})$/u);
  if (!match || /\d/.test(match[2]) || /[@|｜]/.test(clean)) return null;
  const country = match[2].trim();
  if (!LOCATION_HINT.test(country) && !/^(?:city|state|province|county|branch|hq|headquarters|office)$/i.test(country)) return null;
  return { company: match[1].trim(), country };
}

export function organizeExperienceLines(section) {
  if (!isProfessionalExperienceHeading(section.title)) return section;
  const lines = section.lines.map((line) => ({ ...line }));
  for (let index = 0; index < lines.length - 1; index += 1) {
    const company = lines[index].kind === 'text' && resumeCompanyLine(lines[index].text);
    if (!company) continue;

    const summaryParts = [];
    let roleIndex = -1;
    for (let cursor = index + 1; cursor < lines.length && cursor <= index + 6; cursor += 1) {
      const candidate = lines[cursor];
      if (candidate.kind === 'spacer') continue;
      if (candidate.kind !== 'text') break;
      if (resumeEntryLine(candidate.text)) {
        roleIndex = cursor;
        break;
      }
      summaryParts.push(candidate.text);
    }

    const summary = summaryParts.join(' ').replace(/\s+/g, ' ').trim();
    if (roleIndex < 0 || summary.length < 12 || summary.length > 260) continue;
    const role = lines[roleIndex];
    lines.splice(index, roleIndex - index + 1,
      { kind: 'company-entry', ...company },
      { kind: 'company-note', text: summary },
      role);
  }
  return { ...section, lines };
}

export function prepareResumeSections(text = '') {
  const lines = cleanResumeDisplayText(text).replace(/\r\n?/g, '\n').replace(/\u00a0/g, ' ').split('\n');
  const sections = [];
  let current = { title: '', lines: [] };
  const flush = () => {
    while (current.lines.at(-1)?.kind === 'spacer') current.lines.pop();
    if (current.title || current.lines.length) sections.push(organizeExperienceLines(current));
  };

  for (const rawLine of lines) {
    const line = rawLine.replace(/\t/g, '    ').trim();
    const heading = line && resumeHeading(line);
    if (heading) {
      flush();
      current = { title: heading, lines: [] };
      continue;
    }
    if (!line) {
      if (current.lines.length && current.lines.at(-1)?.kind !== 'spacer') current.lines.push({ kind: 'spacer', text: '' });
      continue;
    }
    const expanded = line.replace(/\s*[•●▪◦]\s*/g, '\n• ').split('\n').filter(Boolean);
    for (const value of expanded) {
      const bullet = value.match(/^(?:([-*•●▪◦])|(\d+[.)]))\s+(.+)$/);
      if (bullet) current.lines.push({ kind: 'bullet', marker: bullet[2] || '•', text: bullet[3].trim() });
      else {
        const normalized = value.trim();
        const entry = resumeEntryLine(normalized);
        const previous = current.lines.at(-1);
        if (entry && previous?.kind === 'text' && entry.title.length <= 10 && /[.,]$/.test(previous.text)) {
          previous.text = `${previous.text} ${normalized}`;
        } else {
          current.lines.push({ kind: /^\*\*.+\*\*$/.test(normalized) ? 'lead' : 'text', text: normalized });
        }
      }
    }
  }
  flush();
  return sections.length ? sections : [{ title: '', lines: [{ kind: 'text', text: String(text) }] }];
}

// A company note must read as a company description. Responsibility lines often
// carry business vocabulary ("retail", "group", "provider"), so an explicit
// description signal is required and duty phrasing is rejected outright.
const COMPANY_DESCRIPTION_HINT = /\b(?:is\s+(?:a|an|one\s+of)|are\s+a|a\s+leading|the\s+leading|a\s+trusted|founded|established\s+in|headquarter|subsidiary|part\s+of\s+the|multinational|listed\s+(?:company|group)|specialis|specializ|manufacturer|distributor|provider|retailer|supplier|conglomerate|group\s+of\s+companies|based\s+in)\b|是一家|为一家|隶属于|总部|成立于|专注于|集团旗下|旗下(?:品牌|公司)|为(?:全球|亚洲|区域|国内|新加坡|马来西亚|中国|欧洲|美国)[^。；;]{0,16}(?:集团|公司|企业|品牌|运营商|供应商|制造商|经销商|零售商|平台)/i;
const DUTY_OPENING = /^(?:manag|handl|lead\b|led\b|driv|drove|support|assist|coordinat|collaborat|responsib|overs|develop|implement|execut|deliver|maintain|monitor|prepar|report|liais|negotiat|achiev|improv|reduc|ensur|conduct|perform|plann?\b|organi[sz]|review|analy[sz]|process|standardi[sz]|streamlin|facilitat|provid|train|creat|build|built|work|issu|check|answer|updat|compil|produc|optimi[sz]|partner|spearhead|own\b|负责|管理|协助|统筹|推动|支持|执行|完成|优化|制定|跟进|对接|维护|监控|分析|处理|参与|领导|搭建|建立|组织|协调|汇报|准备|谈判|采购)/i;

export function looksLikeCompanyDescription(text = '') {
  const value = String(text).trim();
  if (!value || value.length > 320) return false;
  if (/^[•●▪◦·.*-]\s*/.test(value)) return false;
  if (DUTY_OPENING.test(value)) return false;
  return COMPANY_DESCRIPTION_HINT.test(value);
}

export function matchCompanyBackground(entry, companyBackgrounds = []) {
  if (!companyBackgrounds?.length || !entry) return null;
  const metaJoined = (entry.meta || []).join(' ').toLowerCase();
  const titleLower = String(entry.title || '').toLowerCase();

  for (const bg of companyBackgrounds) {
    const rawComp = String(bg.company || '').toLowerCase();
    const cleanComp = rawComp.replace(/\b(?:pte\.?\s*ltd|private\s+limited|ltd|limited|inc\.?|corporation|corp\.?|company|group|holdings?|singapore|malaysia|china|asia|sdn\.?\s*bhd)\b/gi, '').trim();
    if (cleanComp && cleanComp.length >= 3 && metaJoined.includes(cleanComp)) return bg;
    if (rawComp && rawComp.length >= 3 && metaJoined.includes(rawComp)) return bg;
  }

  for (const bg of companyBackgrounds) {
    const rawRole = String(bg.role || '').toLowerCase();
    if (rawRole && rawRole.length >= 4 && (titleLower.includes(rawRole) || rawRole.includes(titleLower))) return bg;
  }
  return null;
}

export function companyOverviewText(bg) {
  if (!bg) return null;
  if (bg.companyOverview) return bg.companyOverview;
  return bg.industry && bg.industry !== '未知' ? bg.industry : null;
}

// Business types come from the scored companyBackgrounds enum, so "Unknown" means
// the review could not place the company and no tag should be shown.
export function companyBusinessTypes(bg) {
  return [...new Set((bg?.businessTypes || [])
    .map((value) => String(value || '').trim())
    .filter((value) => value && value.toLowerCase() !== 'unknown'))];
}

function splitSkillItems(values = []) {
  return [...new Set(values.flatMap((value) => String(value || '')
    .split(/\s*(?:[|｜•●▪◦;；])\s*/)
    .map((item) => item.trim())
    .filter(Boolean)))];
}

// A skills section can contain both short keyword lists and full competency
// descriptions. Commas are part of many real skill names and sentences, so they
// must never be treated as card boundaries.
export function groupSkillBlocks(blocks = []) {
  const groups = [];
  let compactItems = [];
  const flushCompactItems = () => {
    if (!compactItems.length) return;
    groups.push({ type: 'compact-grid', items: [...new Set(compactItems)] });
    compactItems = [];
  };

  for (const block of blocks) {
    const values = block.type === 'entry'
      ? [block.title, ...(block.meta || [])]
      : [block.text];
    const items = splitSkillItems(values);
    if (!items.length) continue;
    if (!block.label) {
      compactItems.push(...items);
      continue;
    }

    flushCompactItems();
    const isNarrative = items.length === 1
      && (items[0].length >= 80 || /[.!?。！？]$/.test(items[0]));
    groups.push(isNarrative
      ? { type: 'skill-detail', label: block.label, text: items[0] }
      : { type: 'skill-group', label: block.label, items });
  }
  flushCompactItems();
  return groups;
}

export { buildResumeStructureIndex };
